// src/components/CustomerFlow/Steps/PaymentProcessingStep.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faLock } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import api from '../../../services/api';
import { trackMobilePaymentCardSaved } from '../../../analytics/events';
import { ADVANCE_PAYMENT_MIN_AMOUNT } from '../../../utils/advancePayment';
import {
  MOBILE_PAYMENT_CONTAINER_ID,
  cleanupPaymentWidgetArtifacts,
  clearPaymentWidgetContainer,
  destroyPaymentsInstance,
  loadCirenioPaymentScript,
  reportMobileAuthError,
  watchPaymentWidgetCloseButton,
} from '../../../utils/paymentWidgetUtils';
import './PaymentProcessingStep.css';

const PaymentProcessingStep = ({
  flowData,
  stepStatuses,
  onNext,
  onSessionExpired,
}) => {
  const customerId = flowData?.tokenCustomerId;
  const purchaseRequestId = flowData?.tokenPurchaseRequestId;

  const handoffContext = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('temp_handoffContext') || 'null');
    } catch (e) {
      return null;
    }
  }, []);

  const flowCustomer = flowData?.customer || handoffContext?.customer || {};
  const flowPurchaseRequest = flowData?.purchase_request || handoffContext?.purchase_request;
  const flowDownpayment = flowData?.downpayment || handoffContext?.downpayment;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCardSaved, setIsCardSaved] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isPaymentWidgetOpen, setIsPaymentWidgetOpen] = useState(false);
  const [isSdkScriptReady, setIsSdkScriptReady] = useState(false);

  const paymentsInstanceRef = useRef(null);
  const isOpeningWidgetRef = useRef(false);
  const closeButtonObserverCleanupRef = useRef(null);
  const pollingRunRef = useRef(0);
  const completionTriggeredRef = useRef(false);
  const widgetConfigRef = useRef({});

  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;

  const tokenScope = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('temp_tokenScope') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const digitsOnly = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).replace(/\D/g, '');
  };

  const expectedHolderId = digitsOnly(
    tokenScope.customer_dni
      || tokenScope.dni
      || tokenScope.document_number
      || tokenScope.customer?.dni
      || localStorage.getItem('temp_tokenDni')
      || flowCustomer?.dni
  ) || null;

  const widgetDocument =
    digitsOnly(flowCustomer?.cuil || flowCustomer?.cuilCuit || flowCustomer?.cuit)
    || expectedHolderId
    || null;

  const expectedDownpaymentAmount = Number(
    paymentStatus?.downpayment?.expected_amount
    ?? flowDownpayment?.amount
    ?? 0
  );
  const requiresDownpayment = (
    !!paymentStatus?.downpayment?.required || !!flowDownpayment?.required
  ) && expectedDownpaymentAmount >= ADVANCE_PAYMENT_MIN_AMOUNT;
  const hasLoadedPaymentStatus = paymentStatus !== null;

  widgetConfigRef.current = {
    publicKey,
    widgetDocument,
    requiresDownpayment,
    expectedDownpaymentAmount,
    purchaseRequestId,
    purchaseRequestUid: flowPurchaseRequest?.uid,
    productName: flowPurchaseRequest?.productName,
    firstName: flowCustomer?.firstName,
    lastName: flowCustomer?.lastName,
    email: flowCustomer?.email,
    phone: flowCustomer?.phone,
  };

  const isWidgetCardHolderValid = useCallback((cardHolderIdentification) => {
    if (!expectedHolderId) return true;

    const cardHolderId = digitsOnly(cardHolderIdentification);
    if (!cardHolderId) return true;

    return cardHolderId === expectedHolderId;
  }, [expectedHolderId]);

  const handleRecoverableError = useCallback((error, fallbackMessage) => {
    if (reportMobileAuthError(error, onSessionExpired)) {
      setErrorMessage('');
      return;
    }

    setErrorMessage(fallbackMessage);
  }, [onSessionExpired]);

  const showCardHolderMismatchAlert = useCallback(async () => {
    setIsPaymentWidgetOpen(false);
    isOpeningWidgetRef.current = false;
    setIsSubmitting(false);

    const paymentsInstance = paymentsInstanceRef.current;
    if (paymentsInstance?.closeWidget) {
      await Promise.resolve(paymentsInstance.closeWidget());
    }

    cleanupPaymentWidgetArtifacts(MOBILE_PAYMENT_CONTAINER_ID);

    await new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });

    const customerFirstName = flowCustomer?.firstName || '';
    const customerLastName = flowCustomer?.lastName || '';
    const customerFullName = `${customerFirstName} ${customerLastName}`.trim();

    await Swal.fire({
      html: `
        <div style="text-align: center; padding: 20px 10px;">
          <div style="margin-bottom: 30px; display: flex; justify-content: center; align-items: center;">
            <div style="position: relative; display: inline-block;">
              <i class="bi bi-credit-card" style="color: #6c757d; font-size: 80px; display: inline;"></i>
              <i class="bi bi-x-circle-fill" style="color: #dc3545; font-size: 20px; position: absolute; display: inline; top: 12px; right: -6px; background: white; border-radius: 50%;"></i>
            </div>
          </div>
          <h3 style="font-size: 22px; font-weight: 600; color: #333; margin-bottom: 20px;">
            La tarjeta debe estar a tu nombre
          </h3>
          <p style="font-size: 16px; margin-bottom: 15px; color: #555;">
            ${customerFullName
              ? `La tarjeta ingresada no pertenece a <strong>${customerFullName}</strong>.`
              : 'La tarjeta ingresada no coincide con tu documento.'}
          </p>
          <p style="font-size: 15px; color: #6c757d;">
            Por favor, ingresá una tarjeta de débito que esté a tu nombre.
          </p>
        </div>
      `,
      confirmButtonText: 'Entendido, intentá de nuevo',
      confirmButtonColor: '#435ebe',
      allowOutsideClick: false,
      showCloseButton: false,
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary',
      },
    });
  }, [flowCustomer?.firstName, flowCustomer?.lastName]);

  const startCloseButtonStyleWatcher = useCallback(() => {
    closeButtonObserverCleanupRef.current?.();
    closeButtonObserverCleanupRef.current = watchPaymentWidgetCloseButton();
  }, []);

  useEffect(() => () => {
    closeButtonObserverCleanupRef.current?.();
    closeButtonObserverCleanupRef.current = null;
  }, []);

  useEffect(() => {
    const currentStepStatus = stepStatuses['PaymentStep'];

    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);

      onNext({
        step_name: 'PaymentStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString(),
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  const fetchPaymentStatus = useCallback(async () => {
    const response = await api.get(
      `/customers/${customerId}/purchase-requests/${purchaseRequestId}/payment-status`
    );
    return response.data;
  }, [customerId, purchaseRequestId]);

  useEffect(() => {
    if (!customerId || !purchaseRequestId) {
      return undefined;
    }

    let isActive = true;

    fetchPaymentStatus()
      .then((status) => {
        if (!isActive) return;
        setPaymentStatus(status);
        setErrorMessage('');
      })
      .catch((error) => {
        if (!isActive) return;
        console.error('Error fetching payment status:', error);
        handleRecoverableError(
          error,
          'No pudimos cargar el estado del pago. Revisá tu conexión e intentá de nuevo.'
        );
      });

    return () => {
      isActive = false;
    };
  }, [customerId, purchaseRequestId, fetchPaymentStatus, reloadTrigger, handleRecoverableError]);

  useEffect(() => {
    if (!hasLoadedPaymentStatus || !scriptSrc) {
      return undefined;
    }

    let isActive = true;

    loadCirenioPaymentScript(scriptSrc)
      .then(() => {
        if (isActive) {
          setIsSdkScriptReady(true);
        }
      })
      .catch((error) => {
        if (!isActive) return;
        console.error('Error loading payment SDK:', error);
        setErrorMessage('No pudimos preparar la pantalla de pago. Revisá tu conexión e intentá de nuevo.');
        setIsSdkScriptReady(false);
      });

    return () => {
      isActive = false;
    };
  }, [hasLoadedPaymentStatus, scriptSrc, reloadTrigger]);

  useEffect(() => () => {
    pollingRunRef.current += 1;
    isOpeningWidgetRef.current = false;
    destroyPaymentsInstance(paymentsInstanceRef.current, MOBILE_PAYMENT_CONTAINER_ID);
    paymentsInstanceRef.current = null;
  }, []);

  const completePaymentStepFromWidget = useCallback(async (cardData) => {
    const currentRun = pollingRunRef.current;

    if (completionTriggeredRef.current || currentRun !== pollingRunRef.current) {
      return false;
    }

    completionTriggeredRef.current = true;
    setIsCardSaved(true);
    setErrorMessage('');
    setIsPaymentWidgetOpen(false);
    destroyPaymentsInstance(paymentsInstanceRef.current, MOBILE_PAYMENT_CONTAINER_ID);
    paymentsInstanceRef.current = null;

    const branchId = localStorage.getItem('temp_tokenBranchId');
    const cardBrand = cardData?.source?.brand || cardData?.source?.brand_name;

    trackMobilePaymentCardSaved({
      commerceId: branchId || 'mobile_flow',
      branchId: branchId || undefined,
      flowId: flowData?.flow_id,
      cardBrand,
    });

    const stepData = {
      step_name: 'PaymentStep',
      step_status: 'processed',
      payment_setup_processed: true,
      widget_validated: true,
      requires_downpayment: requiresDownpayment,
      processed_timestamp: new Date().toISOString(),
    };

    setTimeout(() => {
      if (currentRun === pollingRunRef.current && completionTriggeredRef.current) {
        onNext(stepData, 'processed');
      }
    }, 1200);

    return true;
  }, [flowData?.flow_id, onNext, requiresDownpayment]);

  const reloadWidget = useCallback(() => {
    pollingRunRef.current += 1;
    completionTriggeredRef.current = false;
    setErrorMessage('');
    setIsCardSaved(false);
    setIsWidgetLoading(false);
    setIsPaymentWidgetOpen(false);
    setIsSubmitting(false);
    setIsSdkScriptReady(false);

    destroyPaymentsInstance(paymentsInstanceRef.current, MOBILE_PAYMENT_CONTAINER_ID);
    paymentsInstanceRef.current = null;

    setReloadTrigger((prev) => prev + 1);
  }, []);

  const createPaymentsInstance = useCallback(() => {
    const config = widgetConfigRef.current;

    if (!window.CirenioPayments) {
      throw new Error('Payment SDK not loaded');
    }

    if (!config.widgetDocument || !config.purchaseRequestId) {
      throw new Error('Missing payment data');
    }

    cleanupPaymentWidgetArtifacts();
    clearPaymentWidgetContainer(MOBILE_PAYMENT_CONTAINER_ID);

    const prKey = config.purchaseRequestUid || config.purchaseRequestId;
    const paymentsInstance = new window.CirenioPayments(
      config.publicKey,
      config.widgetDocument,
      {
        showCloseButton: true,
        externalId: `purchase_request:${prKey}`,
        flowType: config.requiresDownpayment ? 'payment' : 'tokenization',
        subjectType: 'purchase_request',
        subjectId: String(config.purchaseRequestId),
        subjectUid: config.purchaseRequestUid || undefined,
        paymentContext: config.requiresDownpayment ? 'downpayment_debit_card' : 'tokenization_only',
        advancePayment: config.requiresDownpayment ? {
          amount: config.expectedDownpaymentAmount,
        } : undefined,
        paymentConcept: config.requiresDownpayment
          ? (config.productName || 'Pago inicial de tu compra')
          : undefined,
        paymentDescription: config.requiresDownpayment ? 'Pago inicial de tu compra' : undefined,
        firstName: config.firstName,
        lastName: config.lastName,
        email: config.email,
        phone: config.phone || undefined,
      }
    );

    paymentsInstance.on('widget_start', () => {
      startCloseButtonStyleWatcher();
      setIsWidgetLoading(false);
      setIsPaymentWidgetOpen(true);
    });

    paymentsInstance.on('widget_closed', () => {
      closeButtonObserverCleanupRef.current?.();
      closeButtonObserverCleanupRef.current = null;
      setIsWidgetLoading(false);
      setIsPaymentWidgetOpen(false);
      cleanupPaymentWidgetArtifacts();
    });

    paymentsInstance.on('tokenization_success', async (cardData) => {
      const cardHolderId = cardData?.source?.holder?.identification;

      if (!isWidgetCardHolderValid(cardHolderId)) {
        await showCardHolderMismatchAlert();
        reloadWidget();
        return;
      }

      setIsSubmitting(true);
      await completePaymentStepFromWidget(cardData);
      setIsSubmitting(false);
    });

    paymentsInstance.on('tokenization_error', () => {
      setIsWidgetLoading(false);
      setIsPaymentWidgetOpen(false);
      cleanupPaymentWidgetArtifacts();
      setErrorMessage('Hubo un problema con tu tarjeta. Intentá de nuevo.');
    });

    return paymentsInstance;
  }, [completePaymentStepFromWidget, isWidgetCardHolderValid, reloadWidget, showCardHolderMismatchAlert, startCloseButtonStyleWatcher]);

  const openPaymentWidget = async () => {
    if (
      isOpeningWidgetRef.current
      || isPaymentWidgetOpen
      || isSubmitting
      || isCardSaved
    ) {
      return;
    }

    if (!isSdkScriptReady) {
      setErrorMessage('La pantalla de pago todavía se está preparando. Intentá de nuevo en unos segundos.');
      return;
    }

    if (!widgetDocument) {
      setErrorMessage('Faltan datos para iniciar el pago. Pedile ayuda a tu vendedor.');
      return;
    }

    isOpeningWidgetRef.current = true;
    setErrorMessage('');
    setIsWidgetLoading(true);

    try {
      if (paymentsInstanceRef.current) {
        destroyPaymentsInstance(paymentsInstanceRef.current);
        paymentsInstanceRef.current = null;
      }

      paymentsInstanceRef.current = createPaymentsInstance();

      const opened = await paymentsInstanceRef.current.openWidget();

      startCloseButtonStyleWatcher();

      if (opened === false) {
        setIsPaymentWidgetOpen(false);
        setIsWidgetLoading(false);
        cleanupPaymentWidgetArtifacts();
        setErrorMessage('No pudimos abrir la pantalla de pago. Revisá tu conexión e intentá de nuevo.');
      }
    } catch (error) {
      console.error('Error opening payment widget:', error);
      destroyPaymentsInstance(paymentsInstanceRef.current);
      paymentsInstanceRef.current = null;
      setIsPaymentWidgetOpen(false);
      setIsWidgetLoading(false);
      handleRecoverableError(
        error,
        'No pudimos abrir la pantalla de pago. Revisá tu conexión e intentá de nuevo.'
      );
    } finally {
      isOpeningWidgetRef.current = false;
    }
  };

  const renderErrorAlert = () => {
    if (!errorMessage) return null;

    const canRetry = (
      errorMessage.includes('No pudimos abrir la pantalla de pago')
      || errorMessage.includes('No pudimos cargar el formulario de pago')
      || errorMessage.includes('No pudimos cargar el estado del pago')
      || errorMessage.includes('No pudimos preparar la pantalla de pago')
    );

    if (canRetry) {
      return (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <FontAwesomeIcon icon={faCreditCard} className="me-2" />
          {errorMessage}{' '}
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ textDecoration: 'underline' }}
            onClick={reloadWidget}
          >
            Reintentar
          </button>.
        </div>
      );
    }

    return (
      <div className="alert alert-warning text-center mt-3" role="alert">
        <FontAwesomeIcon icon={faCreditCard} className="me-2" />
        {errorMessage}
      </div>
    );
  };

  const InfoModal = () => {
    if (!showInfoModal) return null;

    return (
      <div className="modal-overlay payment-info-modal-overlay">
        <div className="modal-content bg-white p-4 rounded-3 payment-info-modal-content">
          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              {requiresDownpayment ? 'Requisitos para el pago inicial' : 'Requisitos de tu tarjeta'}
            </h6>
            <ul className="list-unstyled">
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe ser una tarjeta de débito
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar a tu nombre
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar vinculada a la cuenta donde recibís tus ingresos
              </li>
            </ul>
          </div>

          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              ¿Para qué se utilizará?
            </h6>
            <p className="text-muted mb-2">
              {requiresDownpayment
                ? 'La usamos para cobrar el pago inicial de tu compra. Cuando confirmemos el pago, seguimos con el siguiente paso.'
                : 'La usamos como medio alternativo de cobro y queda vinculada para futuras compras.'}
            </p>
          </div>

          <div>
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Seguridad
            </h6>
            <p className="text-muted mb-0">
              Tus datos son tratados con los más altos estándares de seguridad bancaria y están protegidos por la ley de protección de datos personales
            </p>
          </div>

          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowInfoModal(false)}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  };

  const isPrimaryActionDisabled = (
    !isSdkScriptReady
    || isWidgetLoading
    || isPaymentWidgetOpen
    || isSubmitting
    || paymentStatus === null
  );

  const showLaunchCard = !isCardSaved;
  const showButtonSpinner = isWidgetLoading || isSubmitting;

  return (
    <div className="payment-processing-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3>{requiresDownpayment ? 'Hacé el pago inicial' : 'Vinculá tu tarjeta'}</h3>
      </div>

      <div className="step-body">
        <div className="text-center mb-4">
          <p className="lead mb-1">
            {requiresDownpayment ? 'Realizá el pago inicial con tu tarjeta de débito' : 'Vinculá tu tarjeta para los débitos automáticos'}
          </p>
          <p className="lead">
            {requiresDownpayment
              ? 'Usá una tarjeta de débito a tu nombre. Cuando registremos el pago, te llevamos al siguiente paso.'
              : 'Usá una tarjeta de débito a tu nombre, vinculada a la cuenta donde recibís tus ingresos.'}
          </p>

          <div className="text-center">
            <button
              type="button"
              className="btn btn-link text-primary p-0 text-decoration-none"
              onClick={() => setShowInfoModal(true)}
            >
              <i className="bi bi-info-circle me-1"></i>
              {requiresDownpayment ? '¿Por qué pedimos el pago inicial?' : '¿Para qué necesitamos tu tarjeta?'}
            </button>
          </div>
        </div>

        {showLaunchCard && (
          <div className="payment-widget-launch-card text-center">
            <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-primary mb-3" />
            <h5 className="mb-2">
              {requiresDownpayment ? 'Pagar con tu tarjeta de débito' : 'Cargar datos de la tarjeta'}
            </h5>
            <p className="text-muted mb-4">
              {requiresDownpayment
                ? 'Vas a completar el pago en una pantalla segura.'
                : 'Vas a cargar los datos de tu tarjeta en una pantalla segura.'}
            </p>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={openPaymentWidget}
              disabled={isPrimaryActionDisabled}
            >
              {showButtonSpinner && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              )}
              {isSubmitting
                ? 'Validando...'
                : isWidgetLoading
                  ? 'Preparando...'
                  : isPaymentWidgetOpen
                    ? 'Completá el pago'
                    : requiresDownpayment
                      ? 'Ir a pagar'
                      : 'Cargar tarjeta'}
            </button>
            {isPaymentWidgetOpen && (
              <p className="text-muted small mt-3 mb-0">
                Completá el formulario en la pantalla segura. Si la cerrás, podés volver a abrirlo desde acá.
              </p>
            )}
          </div>
        )}

        {renderErrorAlert()}
      </div>

      {isCardSaved && (
        <div className="text-center mt-4">
          <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-success mb-3" />
          <h3 className="text-success">{requiresDownpayment ? '¡Pago registrado!' : '¡Tarjeta registrada!'}</h3>
          <p className="lead">
            {requiresDownpayment
              ? 'Registramos tu pago. Estamos avanzando con tu compra...'
              : 'Registramos tu tarjeta. Estamos avanzando con tu compra...'}
          </p>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Procesando...</span>
          </div>
        </div>
      )}

      <InfoModal />
    </div>
  );
};

export default PaymentProcessingStep;
